import { ENV } from '@/constants';
import { exploreSchemaItemList } from '../exploreSchema';

export const schemaObject_GenerativeFill = {
	'@context': 'https://schema.org',
	'@graph': [
		{
			'@type': 'WebPage',
			'@id': 'https://liftapp.ai/generative-fill',
			name: 'AI Generative Fill — Fill Images online | Lift',
			description:
				'Fill images instantly with AI Generative Fill. No watermark. Upload photo, replace objects, expand backgrounds, change clothes and create stunning visuals with text prompts. Try now!',
			url: 'https://liftapp.ai/generative-fill',
			image: 'https://liftapp.ai/seo-image.webp',
			mainEntity: [
				{ '@id': 'https://liftapp.ai/generative-fill#product' },
				{ '@id': 'https://liftapp.ai/generative-fill#howto' },
				{ '@id': 'https://liftapp.ai/generative-fill#benefits' },
				{ '@id': 'https://liftapp.ai/generative-fill#faq' },
				{ '@id': 'https://liftapp.ai/generative-fill#why-lift' },
				{ '@id': 'https://liftapp.ai/generative-fill#explore-tools' },
				{ '@id': 'https://liftapp.ai/generative-fill#all-in-one' },
			],
		},
		// product
		{
			'@type': 'Product',
			'@id': 'https://liftapp.ai/generative-fill#product',
			name: 'AI Generative Fill Online',
			description:
				'Fill, extend, and transform images with AI in seconds. Get professional results with text prompts, no watermarks.',
			url: 'https://liftapp.ai/generative-fill',
			fileFormat: ['image/jpeg', 'image/png', 'image/webp'],
			memoryRequirements: 'Max file size: 20MB',
			brand: {
				'@type': 'Brand',
				name: 'Lift',
			},
			aggregateRating: {
				'@type': 'AggregateRating',
				ratingValue: '4.8',
				reviewCount: 130000,
				bestRating: '5',
				worstRating: '1',
			},
			interactionStatistic: [
				{
					'@type': 'InteractionCounter',
					interactionType: { '@type': 'InstallAction' },
					userInteractionCount: 13000000,
				},
			],
		},
		// howto
		{
			'@type': 'HowTo',
			'@id': 'https://liftapp.ai/generative-fill#howto',
			name: 'How it works',
			step: [
				{
					'@type': 'HowToStep',
					position: 1,
					name: 'Upload your photos',
					text: "Choose a photo from your device or take one using Lift's app",
				},
				{
					'@type': 'HowToStep',
					position: 2,
					name: 'Let the AI do the work',
					text: 'Watch as Lift fills your images with AI automatically',
				},
				{
					'@type': 'HowToStep',
					position: 3,
					name: 'Customize adjustments',
					text: 'Fine-tune your image with easy-to-use editing tools',
				},
				{
					'@type': 'HowToStep',
					position: 4,
					name: 'Download and use',
					text: 'Download your image or publish it straight to your social feeds',
				},
			],
		},
		// benefit
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/generative-fill#benefits',
			name: 'Who can benefit from AI Generative Fill?',
			description:
				"Lift's AI Generative Fill is useful for professionals, creators, and businesses who want to fill, extend, and transform images with AI-powered generation.",
			itemListOrder: 'Unordered',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'Content creators — Extend backgrounds, add objects, and remove distractions for polished Instagram, TikTok, and YouTube visuals. Create professional-quality posts in minutes without expensive editing software.',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'Designers & Freelancers — Handle routine tasks like extending frames and replacing backgrounds instantly. Generate draft results in minutes instead of hours and send watermark-free files directly to clients.',
				},
				{
					'@type': 'ListItem',
					position: 3,
					name: 'Marketers and SMM managers — Adapt one photo for multiple ad formats and campaigns without new photoshoots. Test creative concepts faster and produce high-volume visuals for banners and landing pages.',
				},
				{
					'@type': 'ListItem',
					position: 4,
					name: 'Small Business Owners — Enhance product photos with clean backgrounds and added space without Photoshop skills. Create professional e-commerce listings that boost click-through rates and conversions.',
				},
				{
					'@type': 'ListItem',
					position: 5,
					name: 'Students & educators — Generate visual content for presentations, posters, and learning materials with minimal skills. Perfect for assignments, collages, and educational projects that need maximum visual impact.',
				},
			],
			image: {
				'@type': 'ImageObject',
				contentUrl:
					'https://liftapp.ai/_next/static/media/benefits.3da6de53.webp',
				name: 'AI Generative Fill by Lift',
				description:
					'Example of how Lift AI Generative Fill creates stunning visuals by filling and extending images.',
			},
		},

		// faq
		{
			'@type': 'FAQPage',
			'@id': 'https://liftapp.ai/generative-fill#faq',
			mainEntity: [
				{
					'@type': 'Question',
					name: 'What is Lift AI Generative Fill?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Lift AI Generative Fill is a premium online tool that instantly fills, extends, and transforms images using AI-powered generation — ideal for extending backgrounds, replacing objects, and creating new visual elements with simple text prompts.',
					},
				},
				{
					'@type': 'Question',
					name: 'How does Lift AI Generative Fill work?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: "Simply upload your photo to Lift's generative fill tool and add a text prompt describing what you want. Our AI automatically fills empty areas, extends backgrounds, or replaces elements in seconds — no advanced photo editing skills needed.",
					},
				},
				{
					'@type': 'Question',
					name: 'Will it keep hair, edges, and fine details?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Yes. Lift AI Generative Fill uses advanced edge detection to preserve delicate elements like hair, fur, shadows, and soft textures — so you get clean, natural-looking results that blend seamlessly with your original image.',
					},
				},
				{
					'@type': 'Question',
					name: 'Can I download the results without a watermark?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: "Absolutely. All images generated or edited in Lift can be exported without watermark when you're on the Pro plan. Download them in high-res PNG or JPG — ready for social media, print, ads, or e-commerce.",
					},
				},
			],
		},

		// why lift
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/generative-fill#why-lift',
			name: "Why Lift's AI Generative Fill stands out",
			description:
				"Lift's Generative Fill combines professional-grade AI, ease of use, and affordability — making it a trusted choice for millions of users worldwide.",
			itemListOrder: 'Unordered',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'High-quality results',
					description:
						'AI-powered technology delivers professional-grade visuals every time',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'Intelligent rendering',
					description:
						'AI tailors the effect to the scene and subject for optimal results',
				},
				{
					'@type': 'ListItem',
					position: 3,
					name: 'Cross-platform support',
					description: 'Available on iOS, Android, and Web for seamless access',
				},
				{
					'@type': 'ListItem',
					position: 4,
					name: 'Budget-friendly',
					description:
						'Save on costly designers with an affordable and intuitive tool',
				},
			],
			image: {
				'@type': 'ImageObject',
				contentUrl:
					'https://liftapp.ai/_next/static/media/whyUse.ae83a89c.webp',
				name: 'Why use Lift AI Generative Fill',
				description:
					"Comparison of Lift's AI Generative Fill benefits — professional quality, AI accuracy, multi-platform access, and cost savings.",
			},
		},
		// explore
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/generative-fill#explore-tools',
			name: 'Explore the AI tools',
			description:
				'A curated list of AI-powered tools for image generation, enhancement, background removal, and more – all available on Lift.',
			itemListOrder: 'Unordered',
			...exploreSchemaItemList,
		},

		{
			'@type': 'SoftwareApplication',
			'@id': 'https://liftapp.ai/generative-fill#all-in-one',
			name: 'Try Lift – AI Content Creation Tools',
			description:
				'Your all-in-one tools to create, edit, and elevate your content. Create stunning content with AI tools, templates, and editing features effortlessly.',
			operatingSystem: 'iOS, Android, Web',
			applicationCategory: 'MultimediaApplication',
			url: `${ENV.LIFT_WEB_APP_URL}/sign-in?auto_login=true`,
			applicationSubCategory: 'Content Creation',
			publisher: {
				'@type': 'Organization',
				name: 'Lift',
				url: 'https://liftapp.ai',
			},
			potentialAction: [
				{
					'@type': 'ViewAction',
					name: 'Open Web App',
					target: `${ENV.LIFT_WEB_APP_URL}/sign-in?auto_login=true`,
				},
			],
		},

		{
			'@type': 'BreadcrumbList',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'Home',
					item: 'https://liftapp.ai',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'AI Generative Fill',
					item: 'https://liftapp.ai/generative-fill',
				},
			],
		},
	],
};
